import React, { useRef, useState } from 'react';
import { Download, Eye, RefreshCw, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import api, { uploadRequestConfig, uploadErrorMessage } from '../../services/api';
import { MAX_FILE_MB } from '../../config/config';

const OnboardingDocumentActions = ({ document, employeeId, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const openDocument = async (download = false) => {
    const previewWindow = window.open('', '_blank');
    if (previewWindow) previewWindow.opener = null;
    try {
      const query = download ? '?download=1' : '';
      const res = await api.get(`/documents/${document._id}/access-url${query}`);
      if (!res.data?.accessUrl) throw new Error('Missing document access URL');
      if (previewWindow && !previewWindow.closed) previewWindow.location.replace(res.data.accessUrl);
      else window.location.assign(res.data.accessUrl);
    } catch (error) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      toast.error(error.response?.data?.message || 'Failed to open document');
    }
  };

  const reupload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${MAX_FILE_MB} MB.`);
      return;
    }

    setBusy(true);
    const data = new FormData();
    data.append('file', file);
    data.append('documentType', document.documentType);
    if (document.documentLabel) data.append('documentLabel', document.documentLabel);
    try {
      const res = await api.post(`/documents/employee/${employeeId}/upload`, data, uploadRequestConfig());
      onChanged?.(res.data.document);
      toast.success('Document replaced successfully');
    } catch (error) {
      toast.error(uploadErrorMessage(error, 'Failed to replace document'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button type="button" onClick={() => openDocument(false)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
        <Eye className="h-3.5 w-3.5" /> View
      </button>
      <button type="button" onClick={() => openDocument(true)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
        <Download className="h-3.5 w-3.5" /> Download
      </button>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
        {busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {busy ? 'Replacing...' : 'Re-upload'}
      </button>
      <input ref={inputRef} type="file" className="sr-only" accept="application/pdf,image/jpeg,image/png" onChange={reupload} />
    </div>
  );
};

export default OnboardingDocumentActions;
